import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PasswordInput } from "@/components/PasswordInput";
import { AUTH } from "@/constants/testIds";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, loginPin, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pinToken, setPinToken] = useState(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const inputCls =
    "w-full border border-[#d9cfb8] bg-[#fbf7ee] px-4 py-3 focus:outline-none focus:border-[#c8a04a] focus:bg-white";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await login(email, password);
      if (res?.requires_pin) {
        setPinToken(res.pin_token);
        setPin("");
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitPin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await loginPin(pinToken, pin);
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error al verificar el PIN";
      setError(detail);
      if (err?.response?.status === 401 && /expiró/i.test(detail)) {
        setPinToken(null);
        setPin("");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#0f1a2c] text-[#f5f0e6]">
      {/* Left brand panel */}
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 30% 30%, rgba(231,200,121,0.18), transparent 60%), linear-gradient(135deg,#0f1a2c 0%,#1c2a3a 70%,#2a3d59 100%)",
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full border-2 border-[#e7c879] flex items-center justify-center">
              <span className="text-[#e7c879] font-serif text-xl tracking-widest">AJAR</span>
            </div>
            <div className="text-sm tracking-[0.3em] text-[#e7c879]">MINISTERIOS</div>
          </div>

          <div>
            <h1 className="text-5xl lg:text-6xl font-serif leading-tight">
              Árboles <span className="text-[#e7c879]">Junto</span><br /> al Río
            </h1>
            <p className="mt-6 text-[#c4b893] max-w-md text-base leading-relaxed">
              Sistema administrativo de la iglesia. Gestión de miembros, captura de ofrendas,
              control de gastos y reportes mensuales en una sola plataforma.
            </p>
          </div>

          <div className="text-xs tracking-widest text-[#8b7e5e]">
            "SERÁ COMO ÁRBOL PLANTADO JUNTO A CORRIENTES DE AGUAS" — Salmo 1:3
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#f5f0e6] text-[#1c1a14]">
        {pinToken ? (
          <form
            onSubmit={onSubmitPin}
            className="w-full max-w-md bg-white border border-[#d9cfb8] shadow-[0_20px_60px_-20px_rgba(15,26,44,0.4)] p-10"
            style={{ borderTop: "4px solid #c8a04a" }}
          >
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={22} className="text-[#c8a04a]" />
              <h2 className="text-2xl font-serif">Verificación en dos pasos</h2>
            </div>
            <p className="text-sm text-[#5d5849] mb-8">
              Ingrese su PIN de seguridad para completar el inicio de sesión.
            </p>

            <label className="block text-xs uppercase tracking-widest text-[#5d5849] mb-2">
              PIN de seguridad
            </label>
            <PasswordInput
              testid={AUTH.pinInput}
              toggleTestid={AUTH.passwordToggle}
              required
              autoFocus
              inputMode="numeric"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              className={`${inputCls} mb-6 tracking-[0.4em] text-lg`}
              placeholder="••••"
            />

            {error && (
              <div
                data-testid={AUTH.errorMsg}
                className="mb-4 px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-sm"
              >
                {error}
              </div>
            )}

            <button
              data-testid={AUTH.pinSubmit}
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] py-3 tracking-widest text-sm uppercase transition disabled:opacity-60"
            >
              {submitting ? "Verificando..." : "Verificar PIN"}
            </button>

            <button
              data-testid={AUTH.pinBack}
              type="button"
              onClick={() => {
                setPinToken(null);
                setPin("");
                setError("");
              }}
              className="mt-4 w-full text-xs uppercase tracking-widest text-[#8b7e5e] hover:text-[#1c1a14]"
            >
              ← Volver al inicio de sesión
            </button>
          </form>
        ) : (
          <form
            onSubmit={onSubmit}
            className="w-full max-w-md bg-white border border-[#d9cfb8] shadow-[0_20px_60px_-20px_rgba(15,26,44,0.4)] p-10"
            style={{ borderTop: "4px solid #c8a04a" }}
          >
            <div className="md:hidden mb-6 text-center">
              <div className="text-2xl font-serif">AJAR</div>
              <div className="text-xs tracking-widest text-[#8b7e5e]">MINISTERIOS</div>
            </div>
            <h2 className="text-2xl font-serif mb-2">Iniciar sesión</h2>
            <p className="text-sm text-[#5d5849] mb-8">Acceso administrativo al sistema</p>

            <label className="block text-xs uppercase tracking-widest text-[#5d5849] mb-2">
              Correo electrónico
            </label>
            <input
              data-testid={AUTH.emailInput}
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`${inputCls} mb-5`}
              placeholder="admin@ajar.com"
            />

            <label className="block text-xs uppercase tracking-widest text-[#5d5849] mb-2">
              Contraseña
            </label>
            <div className="mb-6">
              <PasswordInput
                testid={AUTH.passwordInput}
                toggleTestid={AUTH.passwordToggle}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                data-testid={AUTH.errorMsg}
                className="mb-4 px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-sm"
              >
                {error}
              </div>
            )}

            <button
              data-testid={AUTH.submitButton}
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] py-3 tracking-widest text-sm uppercase transition disabled:opacity-60"
            >
              {submitting ? "Ingresando..." : "Ingresar"}
            </button>

            <div className="mt-8 pt-6 border-t border-[#ece4cf] text-xs text-[#8b7e5e]">
              © {new Date().getFullYear()} Ministerios Árboles Junto al Río
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
